<template>
  <BSkeletonWrapper
    :loading="loading"
  >
    <template #loading>
      <BCard>

        <BCardTitle>
          <BSkeleton
            width="75%"
            height="23px"
          />
        </BCardTitle>

        <BCardTitle>
          <BSkeleton
            width="25%"
            height="18px"
          />
        </BCardTitle>

        <BCardText>
          <div class="d-flex flex-column gap-2 mt-4">
            <div class="d-flex justify-content-between gap-2">
              <div>
                <BSkeleton
                  width="150px"
                  height="18px"
                />
              </div>
              <div>
                <BSkeleton
                  width="75px"
                  height="18px"
                />
              </div>
            </div>
            <BSkeleton
              width="100%"
              height="16px"
            />
          </div>
        </BCardText>

        <BCardText>
          <slot name="footer-loader" />
        </BCardText>
      </BCard>
    </template>

    <BCard>
      <BCardText>
        <div class="d-flex gap-4 mb-3">
          <BAvatar
            v-if="officer.photo_url"
            :src="computedProfilePath"
            variant="primary"
            size="36px"
          />
          <BAvatar
            v-else
            variant="primary"
            size="36px"
            :text="String(officer?.first_name).charAt(0) + String(officer?.last_name).charAt(0)"
          />
          <div class="d-flex flex-column justify-content-center">
            <div
              class="font-weight-bolder"
              style="font-size: 0.8rem;"
            >
              {{ officer?.first_name }} {{ officer?.last_name }}
            </div>
            <div
              class="mt-1"
              style="font-size: 0.7rem;"
            >
              {{ officer?.clusters?.map(cluster => cluster.cluster_name).join(', ') }}
            </div>
          </div>
        </div>
      </BCardText>

      <BCardText>
        <BRow class="gap-y-3">
          <BCol
            v-bind="childColumns"
            cols="12"
            style="font-size: 0.65rem;"
          >
            <div class="d-flex justify-content-between">
              <div>
                <b>{{ numberFormatter(currentProgress.event, '0,0') }}</b> Conducted / <b>{{ numberFormatter(totalProgress.event, '0,0') }}</b> Target
              </div>
              <div>
                <b :class="`text-${eventVariant}`">{{ (eventProgress).toFixed(2) }}</b> %
              </div>
            </div>
          </BCol>

          <BCol
            cols="12"
            class="px-2"
          >
            <div class="border-bottom" />
          </BCol>

        </BRow>
      </BCardText>

      <!-- <BCardText>
      </BCardText> -->

      <BCardText>
        <slot name="footer" />
      </BCardText>
    </BCard>
  </BSkeletonWrapper>
</template>
<script>
import MxFormatter from '@/mixins/formatter'

export default {
  name: 'ASMTasksOfficerTask',

  mixins: [
    MxFormatter
  ],

  props: {
    loading: {
      type: Boolean,
      default: () => false
    },
    childColumns: {
      type: Object,
      default: () => ({})
    },
    officer: {
      type: Object,
      required: true,
      default: () => ({
        id: '',
        first_name: '',
        last_name: '',
        total_buyers: 0
      })
    },
    totalProgress: {
      type: Object,
      required: true,
      default: () => ({
        event: 0,
        customer: 0,
        hectare: 0,
        cost: 0,
        sales: 0
      })
    },
    currentProgress: {
      type: Object,
      required: true,
      default: () => ({
        event: 0,
        customer: 0,
        hectare: 0,
        cost: 0,
        sales: 0
      })
    }
  },

  computed: {
    computedProfilePath () {
      return `${process.env.VUE_APP_API_BASE_URL}/${this.officer.photo_url}`
    },

    eventProgress () {
      const events = this.currentProgress.event / this.totalProgress.event
      if (isNaN(events) || !isFinite(events)) {
        return 0
      }

      return events * 100
    },

    eventVariant () {
      if (this.eventProgress >= 75) {
        return 'success'
      } else if (this.eventProgress >= 31) {
        return 'warning'
      } else if (this.eventProgress >= 1) {
        return 'danger'
      } else {
        return 'dark'
      }
    },

    customerProgress () {
      const customers = this.currentProgress.customer / this.totalProgress.customer
      if (isNaN(customers) || !isFinite(customers)) {
        return 0
      }

      return customers * 100
    },

    customerVariant () {
      if (this.customerProgress >= 75) {
        return 'success'
      } else if (this.customerProgress >= 31) {
        return 'warning'
      } else if (this.customerProgress >= 1) {
        return 'danger'
      } else {
        return 'dark'
      }
    },

    hectareProgress () {
      const hectares = this.currentProgress.hectare / this.totalProgress.hectare
      if (isNaN(hectares) || !isFinite(hectares)) {
        return 0
      }

      return hectares * 100
    },

    hectareVariant () {
      if (this.hectareProgress >= 75) {
        return 'success'
      } else if (this.hectareProgress >= 31) {
        return 'warning'
      } else if (this.hectareProgress >= 1) {
        return 'danger'
      } else {
        return 'dark'
      }
    },

    conversionRate () {
      const rates = this.officer.total_buyers / this.currentProgress.customer
      if (isNaN(rates) || !isFinite(rates)) {
        return 0
      }

      return rates * 100
    },

    conversionRateVariant () {
      if (this.conversionRate >= 75) {
        return 'success'
      } else if (this.conversionRate >= 31) {
        return 'warning'
      } else if (this.conversionRate >= 1) {
        return 'danger'
      } else {
        return 'dark'
      }
    },

    costProgress () {
      const costs = this.currentProgress.cost / this.totalProgress.cost
      if (isNaN(costs) || !isFinite(costs)) {
        return 0
      }

      return costs * 100
    },

    costVariant () {
      if (this.costProgress >= 75) {
        return 'success'
      } else if (this.costProgress >= 31) {
        return 'warning'
      } else if (this.costProgress >= 1) {
        return 'danger'
      } else {
        return 'dark'
      }
    },

    salesProgress () {
      const sales = this.currentProgress.sales / this.totalProgress.sales
      if (isNaN(sales) || !isFinite(sales)) {
        return 0
      }

      return sales * 100
    },

    salesVariant () {
      if (this.salesProgress >= 75) {
        return 'success'
      } else if (this.salesProgress >= 31) {
        return 'warning'
      } else if (this.salesProgress >= 1) {
        return 'danger'
      } else {
        return 'dark'
      }
    }

  },

  methods: {
    variant () {
      // todo: create officer
    }
  }
}
</script>
